import type { AppRouter } from '@autobid/strapi-integration/server/trpc/routers/index'
import type { typeToFlattenedError } from 'zod/v3'
import { isSSG } from '@autobid/strapi-integration/utils/isSSG'
import { TRPCClientError } from '@trpc/client'
import { TRPCError } from '@trpc/server'

interface ReturnedError {
  code: number
  message: string
  handled?: boolean
}

interface TError {
  zodError: typeToFlattenedError<string, string>
}

export function isTRPCError(
  cause: unknown,
): cause is TRPCClientError<AppRouter> & { data: TError } {
  return (cause instanceof TRPCClientError || cause instanceof TRPCError)
}
export function getError(error: unknown): ReturnedError {
  if (isTRPCError(error)) {
    // SSG case - there is no TRPC API after the page is generated so hitting to the API returns error page (HTML)
    if (isSSG()) {
      return {
        code: 404,
        message: 'Not found',
      }
    }

    const firstZodValidationError = (function () {
      const firstError = Object.values(error.data?.zodError?.fieldErrors || {})[0]

      if (!firstError) {
        return
      }
      const fieldName = Object.keys(error.data?.zodError?.fieldErrors || {})[0]

      if (Array.isArray(firstError)) {
        return `${firstError[0]} (${fieldName})`
      }

      return `${firstError} (${fieldName})`
    }())

    return {
      code: error.data?.httpStatus || 500,
      message: firstZodValidationError || error.shape?.message || error.message || 'Unexpected error',
      handled: Boolean(error.data && error.data.handled),
    }
  }

  if (typeof error !== 'object') {
    const regex = /\[[A-Z]+\] ".*?":\s*([^"]+)/
    const match = String(error).match(regex)

    if (match) {
      const [code, ...splittedMessage] = match[1].split(' ')

      return {
        code: Number(code),
        message: splittedMessage.join(' '),
      }
    }

    return {
      code: 500,
      message: error as string,
    }
  }

  if (!error) {
    return {
      code: 500,
      message: error as unknown as string,
    }
  }

  if (
    'statusCode' in error
    && 'statusText' in error
    && error.statusCode
    && error.statusText
  ) {
    return {
      code: Number(error.statusCode),
      message: error.statusText as string,
    }
  }

  if (isNuxtError(error)) {
    return {
      code: error.statusCode,
      message: error.statusMessage || error.message,
    }
  }

  if ('message' in error) {
    return {
      code: 'statusCode' in error ? Number(error.statusCode) : 500,
      message: error.message as string,
    }
  }

  return {
    code: 500,
    message: 'Unexpected error',
  }
}
